import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SortableHeader, sortData } from "../components/SortableHeader";
import ImportButton from "../components/ImportButton";
import { useStandort } from "../hooks/useStandort";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIEN = [
  "Miete",
  "Nebenkosten",
  "Versicherung",
  "Strom",
  "Gas",
  "Wasser",
  "Internet/Telefon",
  "Leasing",
  "Wartung",
  "Sonstiges"
];

export const Fixkosten = () => {
  const { activeStandortId } = useStandort();
  const [fixkosten, setFixkosten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFixkosten, setEditingFixkosten] = useState(null);
  const [showQmCalculator, setShowQmCalculator] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formData, setFormData] = useState({
    kategorie: "Miete",
    bezeichnung: "",
    betrag: "",
    intervall: "monatlich",
    qm_preis: "",
    flaeche_qm: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStandortId]);

  const fetchData = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const response = await axios.get(`${API}/fixkosten`, { params, withCredentials: true });
      setFixkosten(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Fixkosten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      kategorie: "Miete",
      bezeichnung: "",
      betrag: "",
      intervall: "monatlich",
      qm_preis: "",
      flaeche_qm: ""
    });
    setEditingFixkosten(null);
    setShowQmCalculator(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (fk) => {
    setEditingFixkosten(fk);
    setFormData({
      kategorie: fk.kategorie,
      bezeichnung: fk.bezeichnung,
      betrag: fk.betrag.toString(),
      intervall: fk.intervall,
      qm_preis: fk.qm_preis?.toString() || "",
      flaeche_qm: fk.flaeche_qm?.toString() || ""
    });
    setShowQmCalculator(fk.qm_preis && fk.flaeche_qm);
    setDialogOpen(true);
  };

  const calculateFromQm = () => {
    const qmPreis = parseFloat(formData.qm_preis) || 0;
    const flaeche = parseFloat(formData.flaeche_qm) || 0;
    return qmPreis * flaeche;
  };

  const getMonatlichBetrag = () => {
    let betrag = parseFloat(formData.betrag) || 0;
    
    if (showQmCalculator && formData.qm_preis && formData.flaeche_qm) {
      betrag = calculateFromQm();
    }
    
    if (formData.intervall === "jährlich") {
      return betrag / 12;
    }
    return betrag;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bezeichnung) {
      toast.error("Bitte Bezeichnung angeben");
      return;
    }

    let betrag = parseFloat(formData.betrag) || 0;
    if (showQmCalculator && formData.qm_preis && formData.flaeche_qm) {
      betrag = calculateFromQm();
    }

    if (betrag <= 0) {
      toast.error("Bitte einen gültigen Betrag angeben");
      return;
    }

    try {
      const payload = {
        kategorie: formData.kategorie,
        bezeichnung: formData.bezeichnung,
        betrag: betrag,
        intervall: formData.intervall,
        qm_preis: showQmCalculator ? parseFloat(formData.qm_preis) || null : null,
        flaeche_qm: showQmCalculator ? parseFloat(formData.flaeche_qm) || null : null,
        standort_id: editingFixkosten ? (editingFixkosten.standort_id || null) : (activeStandortId || null),
      };

      if (editingFixkosten) {
        await axios.put(`${API}/fixkosten/${editingFixkosten.id}`, payload, { withCredentials: true });
        toast.success("Fixkosten aktualisiert");
      } else {
        await axios.post(`${API}/fixkosten`, payload, { withCredentials: true });
        toast.success("Fixkosten erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Fixkosten wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/fixkosten/${id}`);
      toast.success("Fixkosten gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getTotalMonatlich = () => {
    return fixkosten.reduce((sum, fk) => sum + (fk.monatlich_betrag || 0), 0);
  };

  // Group by category
  const groupedFixkosten = fixkosten.reduce((acc, fk) => {
    if (!acc[fk.kategorie]) {
      acc[fk.kategorie] = [];
    }
    acc[fk.kategorie].push(fk);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="fixkosten-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Fixkosten</h1>
            <p className="page-subtitle">Miete, Nebenkosten und laufende Ausgaben</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-fixkosten-btn"
          >
            <Plus size={16} className="mr-2" />
            Neue Fixkosten
          </Button>
          <ImportButton kategorie="fixkosten" felder={["bezeichnung","betrag","kategorie","intervall"]} onImportSuccess={fetchData} />
        </div>
      </header>

      {/* Summary Card */}
      {fixkosten.length > 0 && (
        <div className="px-8 py-4">
          <div className="bg-[#FFF3E0] border border-[#FFE0B2] p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#E65100] font-medium">Gesamte Fixkosten / Monat</p>
              <p className="font-mono text-2xl text-[#BF360C] font-medium mt-1">
                {formatCurrency(getTotalMonatlich())}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#E65100]">{fixkosten.length} Positionen</p>
              <p className="text-xs text-[#FF8F00]">auf monatlich umgerechnet</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {fixkosten.length === 0 ? (
        <div className="empty-state">
          <Building2 size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Fixkosten vorhanden</h3>
          <p>Erfassen Sie Ihre laufenden Kosten wie Miete und Nebenkosten</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
            data-testid="add-fixkosten-empty-btn"
          >
            <Plus size={16} className="mr-2" />
            Erste Fixkosten erfassen
          </Button>
        </div>
      ) : (
        <div className="data-table-container" data-testid="fixkosten-table">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader label="Kategorie" sortKey="kategorie" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Bezeichnung" sortKey="bezeichnung" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Betrag" sortKey="betrag" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Intervall" sortKey="intervall" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Monatlich" sortKey="monatlich_betrag" sortConfig={sortConfig} onSort={setSortConfig} />
                <th>Details</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortData(fixkosten, sortConfig).map((fk) => (
                <tr key={fk.id} data-testid={`fixkosten-row-${fk.id}`}>
                  <td>
                    <span className="badge badge-neutral">{fk.kategorie}</span>
                  </td>
                  <td className="font-body font-medium">{fk.bezeichnung}</td>
                  <td>{formatCurrency(fk.betrag)}</td>
                  <td className="text-[#858585]">{fk.intervall}</td>
                  <td className="font-medium">{formatCurrency(fk.monatlich_betrag)}</td>
                  <td className="text-xs text-[#858585]">
                    {fk.qm_preis && fk.flaeche_qm && (
                      <span>{fk.flaeche_qm} qm × {formatCurrency(fk.qm_preis)}/qm</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn-icon"
                        onClick={() => openEditDialog(fk)}
                        data-testid={`edit-fixkosten-${fk.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => handleDelete(fk.id)}
                        data-testid={`delete-fixkosten-${fk.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Category Summary */}
      {Object.keys(groupedFixkosten).length > 0 && (
        <div className="px-8 pb-8">
          <h3 className="font-display text-lg font-semibold mb-4 text-[#3A4D39]">Nach Kategorie</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(groupedFixkosten).map(([kategorie, items]) => {
              const total = items.reduce((sum, item) => sum + item.monatlich_betrag, 0);
              return (
                <div key={kategorie} className="bg-white border border-stone-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#858585]">{kategorie}</p>
                  <p className="font-mono text-lg mt-1">{formatCurrency(total)}</p>
                  <p className="text-xs text-[#858585]">{items.length} Position(en)</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingFixkosten ? "Fixkosten bearbeiten" : "Neue Fixkosten"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kategorie</Label>
                <Select 
                  value={formData.kategorie} 
                  onValueChange={(value) => setFormData({...formData, kategorie: value})}
                >
                  <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="fixkosten-kategorie-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KATEGORIEN.map((kat) => (
                      <SelectItem key={kat} value={kat}>{kat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Intervall</Label>
                <Select 
                  value={formData.intervall} 
                  onValueChange={(value) => setFormData({...formData, intervall: value})}
                >
                  <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="fixkosten-intervall-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monatlich">Monatlich</SelectItem>
                    <SelectItem value="jährlich">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Bezeichnung *</Label>
                <Input
                  value={formData.bezeichnung}
                  onChange={(e) => setFormData({...formData, bezeichnung: e.target.value})}
                  placeholder="z.B. Gewerbemiete Hauptstraße 1"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39]"
                  data-testid="fixkosten-bezeichnung-input"
                />
              </div>
            </div>

            {/* QM Calculator Toggle */}
            {(formData.kategorie === "Miete" || formData.kategorie === "Nebenkosten") && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="qm-calculator"
                  checked={showQmCalculator}
                  onChange={(e) => setShowQmCalculator(e.target.checked)}
                  className="rounded border-stone-300"
                  data-testid="qm-calculator-toggle"
                />
                <Label htmlFor="qm-calculator" className="text-sm cursor-pointer">
                  Aus qm-Preis berechnen
                </Label>
              </div>
            )}

            {showQmCalculator ? (
              <div className="grid grid-cols-2 gap-6 p-4 bg-[#F9F8F6] border border-stone-200">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Preis pro qm (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.qm_preis}
                    onChange={(e) => setFormData({...formData, qm_preis: e.target.value})}
                    placeholder="z.B. 15.00"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono bg-transparent"
                    data-testid="fixkosten-qm-preis-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Fläche (qm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.flaeche_qm}
                    onChange={(e) => setFormData({...formData, flaeche_qm: e.target.value})}
                    placeholder="z.B. 120"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono bg-transparent"
                    data-testid="fixkosten-flaeche-input"
                  />
                </div>
                <div className="col-span-2 text-center">
                  <p className="text-sm text-[#5C5C5C]">Berechneter Betrag:</p>
                  <p className="font-mono text-xl font-medium text-[#3A4D39]">
                    {formatCurrency(calculateFromQm())}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Betrag (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.betrag}
                  onChange={(e) => setFormData({...formData, betrag: e.target.value})}
                  placeholder="0.00"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                  data-testid="fixkosten-betrag-input"
                />
              </div>
            )}

            {/* Receipt Summary */}
            <div className="receipt-summary">
              <div className="receipt-header">
                <p className="receipt-title">Umrechnung</p>
              </div>
              <div className="receipt-line">
                <span>Betrag ({formData.intervall}):</span>
                <span>{formatCurrency(showQmCalculator ? calculateFromQm() : parseFloat(formData.betrag) || 0)}</span>
              </div>
              <div className="receipt-divider" />
              <div className="receipt-total">
                <span>Monatlicher Betrag:</span>
                <span className="text-[#3A4D39]">
                  {formatCurrency(getMonatlichBetrag())}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
                data-testid="cancel-fixkosten-btn"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press"
                data-testid="save-fixkosten-btn"
              >
                <Check size={16} className="mr-2" />
                {editingFixkosten ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fixkosten;
